import React from "react";
import type { Article } from "../components/ArticlePage";

// Editorial how-to articles (SEO top/mid-funnel). Each entry is a full,
// genuinely-distinct piece; ArticlePage wraps it in chrome + the standard
// disclaimer. Bodies use JSX so they can cross-link to the county guides,
// the upset-bid calculator, the glossary, and /public for internal-link depth.

const ctaButtonStyle: React.CSSProperties = {
  display: "inline-block",
  textDecoration: "none",
  marginTop: "0.5rem",
};

export const ARTICLES: Record<string, Article> = {
  howToBuy: {
    seoTitle: "How to Buy Tax Foreclosures in North Carolina (2026 Guide) | Parcely",
    seoDescription:
      "A step-by-step guide to buying tax foreclosures in North Carolina — how to find them, do due diligence, win the auction, and survive the 10-day upset bid window.",
    body: (
      <>
        <h1>How to Buy Tax Foreclosures in North Carolina</h1>
        <p className="legal-updated">A step-by-step guide for investors</p>
        <p>
          North Carolina tax foreclosures are one of the few ways to buy property below
          market in a competitive metro like Charlotte — but the process is its own animal,
          with a court-supervised auction and a 10-day bidding window that catches
          out-of-state investors off guard. Here's how it actually works, start to finish.
        </p>

        <h2>1. Know what you're buying</h2>
        <p>
          When an owner stops paying property taxes, the county holds a lien on the
          property. To collect, the county (or a law firm it hires) <em>forecloses</em> that
          lien in Superior Court and sells the property at a public sale. North Carolina is
          not a tax-lien-certificate state — you can't buy a lien and collect interest — and
          it isn't a simple tax-deed state either. It's a tax <em>foreclosure</em> state. If
          that distinction is new to you, read{" "}
          <a href="/tax-deed-vs-tax-lien-nc">tax lien vs tax deed vs NC tax foreclosure</a>{" "}
          first.
        </p>

        <h2>2. Find delinquent and filed properties</h2>
        <p>
          Deals live in a few public places: the county tax office publishes delinquent
          accounts heading toward sale, the foreclosure law firms post their sale notices,
          and the NC eCourts portal holds the case dockets and{" "}
          <a href="/glossary/upset-bid">upset bids</a> as they're filed. None of these tie
          together or tell you which sales are happening this month — which is the whole
          problem. Parcely pulls them into one map; see the data for{" "}
          <a href="/mecklenburg-tax-foreclosures">Mecklenburg</a>,{" "}
          <a href="/cabarrus-tax-foreclosures">Cabarrus</a>,{" "}
          <a href="/gaston-tax-foreclosures">Gaston</a>, and{" "}
          <a href="/lincoln-tax-foreclosures">Lincoln</a> counties.
        </p>

        <h2>3. Do your due diligence before you bid</h2>
        <p>
          You usually can't tour the interior, so underwrite carefully. Pull the assessed
          value (it's public) and compare it to real comps — assessed value is not market
          value. Run a title search: a §105-374 foreclosure names the lienholders and
          generally wipes out most junior liens, but some claims (IRS liens carry a 120-day
          federal redemption right, certain municipal liens) can survive — confirm with a
          title company or attorney. Drive the property, check occupancy, and budget for the
          condition you can't see.
        </p>

        <h2>4. The auction</h2>
        <p>
          The sale is a public auction, usually on the courthouse steps — run by a
          court-appointed commissioner in a §105-374 case, or by the sheriff in a §105-375
          in rem case. There's an opening bid (often the taxes owed plus costs). The
          highest bidder wins the moment — but not the property.
        </p>

        <h2>5. The 10-day upset bid window</h2>
        <p>
          This is the part that trips people up. After the sale is reported, anyone has 10
          days to <strong>upset the bid</strong> by raising it by the greater of 5% or $750
          and posting a deposit — and every upset restarts the 10-day clock, so a property
          can stay open for weeks. Use the{" "}
          <a href="/upset-bid-calculator">upset bid calculator</a> to see exactly what it
          takes to upset (or to defend) a bid.
        </p>

        <h2>6. Confirmation and the deed</h2>
        <p>
          When 10 days pass with no new upset bid, the court confirms the sale, you pay the
          balance, and whoever conducted the sale — the commissioner or the sheriff —
          delivers the deed. Note that the owner can pay the
          taxes and <a href="/glossary/redemption">redeem</a> the property up until the sale
          is confirmed — so a deal isn't truly yours until confirmation.
        </p>

        <h2>The risks worth respecting</h2>
        <p>
          No interior inspection, possible surviving liens, the chance the owner redeems,
          and getting outbid in the upset window are all real. Tax foreclosures reward
          patient investors who underwrite conservatively and track many properties at once
          rather than falling in love with one.
        </p>

        <p>
          <a href="/public" className="btn-primary" style={ctaButtonStyle}>
            Browse NC tax-foreclosure deals →
          </a>
        </p>
      </>
    ),
  },

  timeline: {
    seoTitle: "The NC Tax Foreclosure Timeline: Delinquency to Deed | Parcely",
    seoDescription:
      "How long does a North Carolina tax foreclosure take? A stage-by-stage timeline — from delinquent taxes and the court filing through the auction, upset bids, and the deed.",
    body: (
      <>
        <h1>The North Carolina Tax Foreclosure Timeline</h1>
        <p className="legal-updated">From delinquent taxes to the deed</p>
        <p>
          Tax foreclosures move on their own clock — slower than a bank foreclosure, and
          rarely on a predictable schedule. Here's the sequence, with rough durations, so
          you know where a property sits when you find it.
        </p>

        <h2>Stage 1 — Delinquency (January 6)</h2>
        <p>
          NC property taxes become delinquent after January 6 of the year after they're due,
          and interest starts that day. The county tax collector sends notices, and unpaid
          accounts can sit for months or years. There's no court case yet — just a growing
          debt.
        </p>

        <h2>Stage 2 — The county files (months to years later)</h2>
        <p>
          To actually collect, the county (or its law firm) files a foreclosure action in
          Superior Court — a §105-374 civil action or a §105-375{" "}
          <a href="/glossary/in-rem-foreclosure">in rem</a> proceeding. This is the moment a
          public case number first exists. A parcel can be delinquent for years before
          anyone files, which is why early-stage properties are hard to find by hand.
        </p>

        <h2>Stage 3 — Judgment and notice of sale</h2>
        <p>
          The court enters judgment, and a{" "}
          <a href="/glossary/notice-of-sale">notice of sale</a> is published with a specific
          date, time, and place. This is when the property becomes a concrete, dated
          opportunity.
        </p>

        <h2>Stage 4 — The auction</h2>
        <p>
          The property is sold at public auction, usually at the courthouse — by a
          court-appointed commissioner in a §105-374 case, or by the sheriff in a §105-375
          in rem case. The high bid is reported to the court — but the sale isn't final.
        </p>

        <h2>Stage 5 — The 10-day upset bid window (often weeks)</h2>
        <p>
          The <a href="/glossary/upset-bid">upset bid</a> period runs 10 days, and every
          qualifying upset restarts it. With active competition this stage routinely
          stretches to several weeks. This is where most of the real bidding happens.
        </p>

        <h2>Stage 6 — Confirmation and deed (≈30+ days after the sale)</h2>
        <p>
          Once 10 quiet days pass, the court confirms the sale, the winner pays, and the
          deed is delivered. From the first filing to the deed is commonly several months —
          plan your capital accordingly.
        </p>

        <p>
          <a href="/public" className="btn-primary" style={ctaButtonStyle}>
            See where NC properties sit in the timeline →
          </a>
        </p>
      </>
    ),
  },

  lienVsDeed: {
    seoTitle: "Tax Lien vs Tax Deed vs NC Tax Foreclosure — Explained | Parcely",
    seoDescription:
      "North Carolina doesn't sell tax lien certificates or simple tax deeds — it's a tax foreclosure state. Here's the difference and what it means for investors.",
    body: (
      <>
        <h1>Tax Lien vs Tax Deed vs North Carolina's Tax Foreclosure</h1>
        <p className="legal-updated">Why NC works differently than the books say</p>
        <p>
          If you learned tax-sale investing from a national course, North Carolina will
          confuse you — because NC is neither a tax-lien state nor a classic tax-deed state.
          Getting this right saves you from chasing a product that doesn't exist here.
        </p>

        <h2>Tax lien certificate states</h2>
        <p>
          In tax-lien states, the county sells the <em>lien</em> to an investor. You don't
          get the property — you get the right to collect the back taxes plus a
          statutory interest rate, and if the owner never pays, you can eventually foreclose.
          You're effectively buying a high-yield, redemption-backed loan. North Carolina
          does <strong>not</strong> do this. You cannot buy an NC tax lien certificate.
        </p>

        <h2>Tax deed states</h2>
        <p>
          In tax-deed states, the county skips the certificate and sells the{" "}
          <em>property</em> (the deed) at auction. Highest bidder takes title, often with
          limited or no redemption period.
        </p>

        <h2>North Carolina: tax foreclosure</h2>
        <p>
          NC is a tax <em>foreclosure</em> state. The county holds a lien for unpaid taxes
          and forecloses it through the courts — either a mortgage-style §105-374 action or
          an in rem §105-375 proceeding — then sells the property at a court-supervised
          public sale. The twist that defines NC: the{" "}
          <a href="/upset-bid-calculator">10-day upset bid window</a>, where the winning bid
          can be raised again and again before the sale is confirmed. So you end up with the
          property (like a tax deed), but you get there through a court process with
          competitive upset bidding (unlike a one-and-done deed auction).
        </p>

        <h2>What it means for you</h2>
        <p>
          Don't come to NC looking for lien certificates or interest yields — they're not
          here. Come looking for below-market <em>property</em> at court-supervised sales,
          and budget for the upset-bid fight. Start with our{" "}
          <a href="/mecklenburg-tax-foreclosures">NC tax-foreclosure guide</a> for the full
          mechanics.
        </p>

        <p>
          <a href="/public" className="btn-primary" style={ctaButtonStyle}>
            Browse NC tax-foreclosure properties →
          </a>
        </p>
      </>
    ),
  },

  preForeclosure: {
    seoTitle: "How to Find Pre-Foreclosures in Charlotte, NC | Parcely",
    seoDescription:
      "Where to find pre-foreclosures in Charlotte and Mecklenburg County — delinquent tax lists, court filings, and early-stage properties before they hit the auction.",
    body: (
      <>
        <h1>How to Find Pre-Foreclosures in Charlotte</h1>
        <p className="legal-updated">Get there before the auction</p>
        <p>
          The least-competitive deals are the ones nobody has found yet — properties that
          are delinquent or freshly filed but not yet at auction. Here's where to look in
          Charlotte and Mecklenburg County, and why early matters.
        </p>

        <h2>What "pre-foreclosure" means here</h2>
        <p>
          For tax foreclosures, pre-foreclosure is the window between a property going
          delinquent and the actual sale — the owner still has time to pay, and the property
          hasn't been bid on. Reach owners or underwrite during this window and you're ahead
          of the crowd that only watches auction lists.
        </p>

        <h2>Where to look</h2>
        <p>
          Three public sources: the <strong>Mecklenburg County tax office</strong> publishes
          delinquent real-property accounts; the <strong>NC eCourts portal</strong> shows
          foreclosure cases the moment they're filed (a{" "}
          <a href="/glossary/lis-pendens">lis pendens</a> is your earliest signal); and the{" "}
          <strong>foreclosure law firms</strong> post upcoming sales. The catch is timing —
          by the time a sale is posted, the early advantage is gone.
        </p>

        <h2>How Parcely surfaces early-stage properties</h2>
        <p>
          Parcely flags Mecklenburg properties at the early "notice filed" stage — filed in
          court but not yet sold — so you can work them before the auction crowd shows up.
          See the{" "}
          <a href="/public/mecklenburg-nc">Mecklenburg foreclosure data</a>, and read the
          full <a href="/mecklenburg-tax-foreclosures">Mecklenburg tax-foreclosure guide</a>{" "}
          for how the stages work.
        </p>

        <h2>A word on approach</h2>
        <p>
          Pre-foreclosure outreach touches people in a hard spot — keep it respectful and
          compliant. The edge is information and timing, not pressure.
        </p>

        <p>
          <a href="/public/mecklenburg-nc" className="btn-primary" style={ctaButtonStyle}>
            See early-stage Mecklenburg properties →
          </a>
        </p>
      </>
    ),
  },
};
